import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Message, Loader } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const ManageDisabledModelsModal = ({ open, onClose, channel }) => {
  const [loading, setLoading] = useState(false);
  const [disabledModels, setDisabledModels] = useState([]);
  const [channelModels, setChannelModels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && channel) {
      loadDisabledModels();
      parseChannelModels();
    }
  }, [open, channel]);

  const parseChannelModels = () => {
    if (!channel || !channel.models) return;
    
    let models = [];
    if (typeof channel.models === 'string') {
      models = channel.models.split(',').map(m => m.trim()).filter(m => m);
    } else if (Array.isArray(channel.models)) {
      models = channel.models;
    }
    
    setChannelModels(models);
  };

  const loadDisabledModels = async () => {
    if (!channel) return;
    
    try {
      setLoading(true);
      const res = await API.get(`/api/channel/${channel.id}/disabled-models`);
      if (res.data.success) {
        setDisabledModels(res.data.data || []);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleModel = async (modelName) => {
    if (!channel || saving) return;
    
    try {
      setSaving(true);
      const isDisabled = disabledModels.includes(modelName);
      
      if (isDisabled) {
        // 启用模型
        const res = await API.post(`/api/channel/${channel.id}/enable-model`, {
          model: modelName
        });
        
        if (res.data.success) {
          setDisabledModels(prev => prev.filter(m => m !== modelName));
          showSuccess(`模型 ${modelName} 已启用`);
        } else {
          showError(res.data.message);
        }
      } else {
        // 禁用模型
        const res = await API.post(`/api/channel/${channel.id}/disable-model`, {
          model: modelName
        });
        
        if (res.data.success) {
          setDisabledModels(prev => [...prev, modelName]);
          showSuccess(`模型 ${modelName} 已禁用`);
        } else {
          showError(res.data.message);
        }
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const isDisabled = (modelName) => {
    return disabledModels.includes(modelName);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
    >
      <Modal.Header>
        管理禁用模型 - {channel?.name || channel?.type} (ID: {channel?.id})
      </Modal.Header>
      <Modal.Content scrolling>
        {loading ? (
          <Loader active inline />
        ) : (
          <>
            <Message info>
              <Message.Header>使用说明</Message.Header>
              <p>勾选的模型将被禁用，用户调用这些模型时会收到明确的错误提示。</p>
            </Message>
            
            {channelModels.length === 0 ? (
              <Message warning>该渠道没有配置任何模型</Message>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {channelModels.map((modelName) => (
                  <Checkbox
                    key={modelName}
                    label={modelName}
                    checked={isDisabled(modelName)}
                    onChange={() => toggleModel(modelName)}
                    disabled={saving}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: isDisabled(modelName) ? '#fff3cd' : '#f8f9fa',
                      borderRadius: '4px',
                      border: isDisabled(modelName) ? '1px solid #ffc107' : '1px solid #dee2e6',
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          关闭
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ManageDisabledModelsModal;
